import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LogoStatus } from './LogoStatus';
import { AIAssistantModal } from './AIAssistantModal';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/8b0zrav9_LAFAYETTE_LOGO-04.jpg';
const SCROLL_THRESHOLD = 50;

export const Navigation = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAIOpen, setIsAIOpen] = useState(false);
  const openAI = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setIsAIOpen(true);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#programs', label: t('nav.programs') },
    { href: '#values', label: t('nav.values') },
    { href: '#skills', label: t('nav.skills') },
    { href: '#admissions', label: t('nav.admissions') },
    { href: '#campus', label: t('nav.campus') },
    { href: '#housing', label: t('nav.housing') },
    { href: '#access', label: t('nav.access') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
  }, [setIsScrolled, SCROLL_THRESHOLD]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-nav shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between pt-6 pb-3">
            {/* Logo */}
            <motion.a
              href="#"
              data-testid="logo-link"
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <div
                className={`w-12 h-12 rounded-full overflow-hidden transition-all duration-300 ${
                  isScrolled
                    ? 'bg-white shadow-sm ring-1 ring-slate-200'
                    : 'bg-white/95 ring-1 ring-white/40 shadow-lg shadow-black/20'
                }`}
              >
                <img
                  src={LOGO_URL}
                  alt="Lafayette International School logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <span className={`font-playfair font-bold text-lg block leading-tight ${
                  isScrolled ? 'text-slate-900' : 'text-white'
                }`}>
                  Lafayette
                </span>
                <span className={`text-xs tracking-wider ${
                  isScrolled ? 'text-slate-600' : 'text-white/80'
                }`}>
                  INTERNATIONAL SCHOOL
                </span>
                <LogoStatus dark={isScrolled} />
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  data-testid={`nav-link-${link.href.replace('#', '')}`}
                  onClick={() => scrollToSection(link.href)}
                  className={`font-inter text-sm font-medium gold-underline transition-colors ${
                    isScrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <LanguageSwitcher dark={isScrolled} />
              <a
                href="https://mylafayetteadmissions.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="apply-now-nav-btn"
                className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full px-6 py-2 text-sm btn-shine transition-colors"
              >
                {t('nav.applyNow')}
              </a>
              <button
                type="button"
                onClick={openAI}
                data-testid="chatgpt-assistant-btn"
                className={`hidden xl:inline-flex items-center gap-1.5 font-inter text-sm font-medium rounded-full px-4 py-2 border transition-colors ${
                  isScrolled
                    ? 'border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400 bg-white/70'
                    : 'border-white/20 text-white/80 hover:text-white hover:border-white/40 bg-white/5 backdrop-blur-sm'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                <span>{t('nav.aiAssistant')}</span>
              </button>
            </div>

            {/* Mobile Controls */}
            <div className="lg:hidden flex items-center gap-2">
              <LanguageSwitcher dark={isScrolled} />
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg ${
                  isScrolled ? 'text-slate-900' : 'text-white'
                }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-lg pt-24 px-6 overflow-y-auto pb-8">
              <div className="flex flex-col gap-4">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`mobile-nav-link-${link.href.replace('#', '')}`}
                    onClick={() => scrollToSection(link.href)}
                    className="text-white text-xl font-playfair text-left py-2 border-b border-white/10"
                  >
                    {link.label}
                  </motion.button>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <a
                    href="https://mylafayetteadmissions.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-apply-btn"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-center bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full py-4 text-lg mt-4 transition-colors"
                  >
                    {t('nav.applyNow')}
                  </a>
                  <button
                    type="button"
                    onClick={openAI}
                    data-testid="mobile-ai-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 font-inter text-base rounded-full py-3.5 border border-white/20 text-white/90 hover:text-white hover:border-white/40 bg-white/5 transition-colors"
                  >
                    <Sparkles className="w-4 h-4 text-violet-300" />
                    {t('nav.aiAssistant')}
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AIAssistantModal open={isAIOpen} onClose={() => setIsAIOpen(false)} />
    </>
  );
};
